import PropTypes from 'prop-types';
import { List, ListItem } from 'framework7-react';
import React, { useEffect, useState } from 'react';

import { I18n } from 'Locales';
import { TextField } from 'Components';
import { ColumnView } from 'Containers';
import { POST_CODE_REGEX, REGIONS } from 'Constants';
import { valueSanitization, FormattedLabel } from 'Helpers';

import '../style.scss';
import { getFieldProp, getIconColor } from '../../../shared';

const sanitizedAddress = ({ setValues, value }, e) => {
  const sanitizedValue = valueSanitization(e);
  setValues(value, { AddressLine1: sanitizedValue });
};

// this function is used to handle the selected city from the list and update the value in the store
const handleSelectedCity = ({ setSearchCity, setValues, value, item, setOnFocusCity }) => {
  setValues(value, { City: item });
  setSearchCity(item);
  setOnFocusCity && setOnFocusCity(false);
};

const AddressFields = props => {
  const {
    cities,
    country,
    enableAddressFields,
    fields,
    isOffline,
    isUnder10Chars,
    isValid,
    required,
    setValues,
    shouldShowError,
    updateIsUnder10Chars,
    addressFoundWithLoqate = false,
    value
  } = props;
  const isUkOrUsRegions = REGIONS.UK.includes(country) || REGIONS.US.includes(country);
  const fieldDisabled = !enableAddressFields && !isOffline;
  const [searchCity, setSearchCity] = useState('');
  const [onFocusCity, setOnFocusCity] = useState(false);
  const searchParts = searchCity?.toLowerCase()?.split(/\s+/);
  const filteredCities = cities?.filter(city => {
    const fullCity = city?.toLowerCase();
    return searchParts?.every(part => fullCity.includes(part));
  });

  let addressValues = value;
  if (!addressValues) {
    addressValues = {
      City: '',
      AddressLine1: '',
      AddressLine2: '',
      AddressLine3: '',
      PostCode: '',
      search_address: ''
    };
  }

  const cityProp = getFieldProp(fields, 'City');
  const line1Prop = getFieldProp(fields, 'AddressLine1');
  const line2Prop = getFieldProp(fields, 'AddressLine2');
  const line3Prop = getFieldProp(fields, 'AddressLine3');
  const postCodeProp = getFieldProp(fields, 'PostCode');

  const { City, AddressLine1, AddressLine2, AddressLine3, PostCode } = addressValues;

  useEffect(() => {
    !isUkOrUsRegions && AddressLine1 !== '' && updateIsUnder10Chars(AddressLine1);
  }, []);

  useEffect(() => {
    setSearchCity(City);
  }, [City]);

  const trimmedPostCode = PostCode?.replace(/\s+/g, '').trim();

  const postCodeValidation = {
    FR: PostCode?.length > 0 && !POST_CODE_REGEX.FR.test(PostCode),
    IE: PostCode?.length > 0 && !POST_CODE_REGEX.IE.test(PostCode),
    BE: PostCode?.length > 0 && !POST_CODE_REGEX.BE.test(PostCode),
    LU: PostCode?.length > 0 && !POST_CODE_REGEX.LU.test(PostCode),
    UK: trimmedPostCode?.length > 0 && !POST_CODE_REGEX.UK.test(trimmedPostCode)
  };

  return (
    <ColumnView>
      <div className="w-full">
        <TextField
          disabled={fieldDisabled}
          label={FormattedLabel({ required, name: I18n.t('flow:components.address.line1') })}
          type={'text'}
          placeholder={line1Prop?.placeholder}
          value={AddressLine1}
          onChange={e => setValues(value, { AddressLine1: e.target.value })}
          onBlur={sanitizedAddress.bind(null, { setValues, value })}
          errorMessageForce={!(!isOffline && isUkOrUsRegions && addressFoundWithLoqate) || !isValid}
          errorMessage={
            !isValid
              ? shouldShowError && AddressLine1 === ''
                ? I18n.t('flow:generalError')
                : isUnder10Chars && !isUkOrUsRegions
                ? I18n.t('validations:min10Characters')
                : ''
              : ''
          }
          icon="material:check"
          iconColor={getIconColor(
            !isValid
              ? shouldShowError && AddressLine1 === ''
                ? true
                : isUnder10Chars && !isUkOrUsRegions
                ? true
                : false
              : false,
            AddressLine1
          )}
        />

        <TextField
          disabled={fieldDisabled}
          label={I18n.t('flow:components.address.line2')}
          type={'text'}
          placeholder={line2Prop?.placeholder}
          value={AddressLine2}
          onChange={e => setValues(value, { AddressLine2: e.target.value })}
          icon="material:check"
        />

        <TextField
          disabled={fieldDisabled}
          label={I18n.t('flow:components.address.line3')}
          type={'text'}
          placeholder={line3Prop?.placeholder}
          value={AddressLine3}
          onChange={e => setValues(value, { AddressLine3: e.target.value })}
          icon="material:check"
        />

        <TextField
          disabled={fieldDisabled}
          label={FormattedLabel({ required, name: I18n.t('flow:components.address.postalCodeLabel') })}
          type={'text'}
          placeholder={postCodeProp?.placeholder}
          value={PostCode}
          onChange={e => setValues(value, { PostCode: e.target.value })}
          errorMessageForce={(shouldShowError && PostCode === '') || (postCodeValidation[country] && !isValid)}
          errorMessage={
            shouldShowError && PostCode === '' ? I18n.t('flow:generalError') : I18n.t(`validations:postCode${country}`)
          }
          icon="material:check"
          iconColor={getIconColor(
            (shouldShowError && PostCode === '') || (postCodeValidation[country] && !isValid),
            PostCode
          )}
        />

        <TextField
          disabled={fieldDisabled}
          label={FormattedLabel({ required, name: I18n.t('flow:components.address.cityLabel') })}
          type={'text'}
          placeholder={cityProp?.placeholder}
          value={searchCity}
          onFocus={setOnFocusCity.bind(null, true)}
          onChange={e => handleSelectedCity({ setSearchCity, setValues, value, item: e.target.value })}
          errorMessageForce={shouldShowError && City === ''}
          errorMessage={I18n.t('flow:generalError')}
          icon="material:check"
          iconColor={getIconColor(shouldShowError && City === '', City)}
        />

        {!fieldDisabled && City !== '' && !isUkOrUsRegions && onFocusCity && (
          <>
            <List className="mt-0 cities-list">
              {filteredCities?.length > 0
                ? filteredCities.map((item, index) => (
                    <ListItem
                      key={index}
                      onClick={handleSelectedCity.bind(null, {
                        setSearchCity,
                        setValues,
                        value,
                        item,
                        setOnFocusCity
                      })}>
                      {item}
                    </ListItem>
                  ))
                : searchCity && <div className="empty-list">{I18n.t('general:noResultsFound')}</div>}
            </List>
          </>
        )}
      </div>
    </ColumnView>
  );
};

AddressFields.propTypes = {
  addressFoundWithLoqate: PropTypes.bool,
  cities: PropTypes.array,
  component_key: PropTypes.any,
  country: PropTypes.string,
  currentCheckInAdress: PropTypes.array,
  enableAddressFields: PropTypes.bool,
  fields: PropTypes.array,
  isOffline: PropTypes.bool,
  isUnder10Chars: PropTypes.bool,
  isValid: PropTypes.bool,
  name: PropTypes.string,
  required: PropTypes.bool,
  setValues: PropTypes.func,
  shouldShowError: PropTypes.bool,
  updateIsUnder10Chars: PropTypes.func,
  updateValue: PropTypes.func,
  value: PropTypes.oneOfType(PropTypes.string, PropTypes.object)
};

export default AddressFields;
